export class ApiError extends Error {
  status: number;
  code: string;
  requestId?: string;
  details?: unknown;
  constructor(status: number, code: string, message: string, requestId?: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}

let accessToken: string | null = null;
// the CMS zone this page converts times with (from /me); every request names it, so the API refuses a
// write converted with a zone that changed since the page loaded
let clientZone: string | null = null;
export function setClientZone(tz: string | null) { clientZone = tz; }
let refreshing: Promise<boolean> | null = null;
// a listener is told why the session was lost: SESSION_REPLACED = a newer login of this account elsewhere
const listeners = new Set<(code: string) => void>();
// the error code of the last refresh the server refused ('' = none, or no answer)
let refreshCode = '';

export const setAccessToken = (t: string | null) => { accessToken = t; };
export const onSessionLost = (fn: (code: string) => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
export const lastRefreshCode = () => refreshCode;

function sessionLost(code: string | undefined) {
  accessToken = null;
  const why = code === 'SESSION_REPLACED' || refreshCode === 'SESSION_REPLACED' ? 'SESSION_REPLACED' : code ?? 'UNAUTHORIZED';
  listeners.forEach((f) => f(why));
}

async function doRefresh(): Promise<boolean> {
  refreshCode = '';
  const r = await fetch('/api/admin/auth/refresh', { method: 'POST', headers: { 'x-cms-csrf': '1' }, credentials: 'same-origin' });
  if (!r.ok) {
    const j = await r.json().catch(() => null) as { error?: { code?: string } } | null;
    refreshCode = j?.error?.code ?? 'HTTP_' + r.status;
    return false;
  }
  const j = await r.json();
  accessToken = j.data.accessToken;
  return true;
}

// The refresh token rotates on every use, so two tabs refreshing at once would make one of them
// present a spent token and lose the session: refreshes are serialized across tabs.
export async function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      return await (navigator.locks ? navigator.locks.request('cms-refresh', doRefresh) : doRefresh());
    } catch {
      return false;
    } finally {
      setTimeout(() => { refreshing = null; }, 0);
    }
  })();
  return refreshing;
}

type Query = Record<string, string | number | boolean | undefined | null>;

function qs(q?: Query) {
  if (!q) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? '?' + s : '';
}

async function request<T>(method: string, path: string, body?: unknown, q?: Query, retried = false): Promise<T> {
  const headers: Record<string, string> = {};
  if (accessToken) headers.authorization = 'Bearer ' + accessToken;
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (clientZone) headers['x-cms-tz'] = clientZone;
  let r: Response;
  try {
    r = await fetch(path + qs(q), { method, headers, body: body === undefined ? undefined : JSON.stringify(body), credentials: 'same-origin' });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Không kết nối được máy chủ CMS');
  }
  const rid = r.headers.get('x-request-id') ?? undefined;
  let j: { success: boolean; data?: T; error?: { code: string; message: string; details?: unknown } } | null = null;
  try {
    j = await r.json();
  } catch {
    j = null;
  }
  // replaced: this browser may hold the newer session (another tab logged in), so the refresh is tried first
  if (r.status === 401 && !retried && ['UNAUTHORIZED', 'SESSION_EXPIRED', 'SESSION_REPLACED'].includes(j?.error?.code ?? '')) {
    if (await refreshSession()) return request<T>(method, path, body, q, true);
    sessionLost(j?.error?.code);
  }
  if (!r.ok || !j?.success) {
    throw new ApiError(r.status, j?.error?.code ?? 'HTTP_' + r.status, j?.error?.message ?? 'Lỗi ' + r.status, rid, j?.error?.details);
  }
  return j.data as T;
}

export const api = {
  get: <T>(path: string, q?: Query) => request<T>('GET', path, undefined, q),
  post: <T>(path: string, body: unknown = {}) => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
  // a file as the raw request body (its own content type): the bytes never become a string
  async upload<T>(path: string, file: Blob, retried = false): Promise<T> {
    const headers: Record<string, string> = { 'content-type': file.type || 'application/octet-stream' };
    if (accessToken) headers.authorization = 'Bearer ' + accessToken;
    if (clientZone) headers['x-cms-tz'] = clientZone;
    let r: Response;
    try {
      r = await fetch(path, { method: 'POST', headers, body: file, credentials: 'same-origin' });
    } catch {
      throw new ApiError(0, 'NETWORK', 'Không kết nối được máy chủ CMS');
    }
    if (r.status === 401 && !retried && (await refreshSession())) return api.upload<T>(path, file, true);
    const j = await r.json().catch(() => null) as { success: boolean; data?: T; error?: { code: string; message: string } } | null;
    if (!r.ok || !j?.success) {
      if (r.status === 413) throw new ApiError(413, 'ANH_QUA_LON', 'Ảnh quá lớn');
      throw new ApiError(r.status, j?.error?.code ?? 'HTTP_' + r.status, j?.error?.message ?? 'Lỗi ' + r.status, r.headers.get('x-request-id') ?? undefined);
    }
    return j.data as T;
  },
  // an authenticated binary GET (image preview); the caller revokes the object URL
  async blobUrl(path: string, retried = false): Promise<string> {
    const headers: Record<string, string> = accessToken ? { authorization: 'Bearer ' + accessToken } : {};
    const r = await fetch(path, { headers, credentials: 'same-origin' });
    if (r.status === 401 && !retried && (await refreshSession())) return api.blobUrl(path, true);
    if (!r.ok) throw new ApiError(r.status, 'HTTP_' + r.status, 'Lỗi ' + r.status);
    return URL.createObjectURL(await r.blob());
  },
  // file exports (CSV): same auth and refresh as JSON calls, saved through a temporary link
  async download(path: string, q: Query, fallbackName: string, retried = false): Promise<void> {
    const headers: Record<string, string> = accessToken ? { authorization: 'Bearer ' + accessToken } : {};
    let r: Response;
    try {
      r = await fetch(path + qs(q), { headers, credentials: 'same-origin' });
    } catch {
      throw new ApiError(0, 'NETWORK', 'Không kết nối được máy chủ CMS');
    }
    if (r.status === 401 && !retried && (await refreshSession())) return api.download(path, q, fallbackName, true);
    if (!r.ok) {
      const j = await r.json().catch(() => null) as { error?: { code: string; message: string } } | null;
      throw new ApiError(r.status, j?.error?.code ?? 'HTTP_' + r.status, j?.error?.message ?? 'Lỗi ' + r.status);
    }
    const name = /filename="([^"]+)"/.exec(r.headers.get('content-disposition') ?? '')?.[1] ?? fallbackName;
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};

export type StreamEvent = { topic: string; data: unknown; at: string };

// SSE over fetch (EventSource cannot send the bearer token). Reconnects until stopped.
export function openStream(onEvent: (e: StreamEvent) => void, onState?: (live: boolean) => void): () => void {
  let stopped = false;
  let ctrl: AbortController | null = null;
  (async () => {
    let wait = 1000;
    while (!stopped) {
      ctrl = new AbortController();
      try {
        if (!accessToken) await refreshSession();
        const r = await fetch('/api/admin/stream', { headers: { authorization: 'Bearer ' + accessToken }, signal: ctrl.signal });
        if (r.status === 401) {
          const j = await r.json().catch(() => null) as { error?: { code?: string } } | null;
          // the server refused the refresh too: the session is over (a newer login, logout): stop and tell the page
          if (!(await refreshSession()) && refreshCode) { stopped = true; sessionLost(j?.error?.code); break; }
          throw new Error('401');
        }
        if (!r.ok || !r.body) throw new Error('stream ' + r.status);
        onState?.(true);
        wait = 1000;
        const reader = r.body.pipeThrough(new TextDecoderStream()).getReader();
        let buf = '';
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += value;
          let i: number;
          while ((i = buf.indexOf('\n\n')) >= 0) {
            const chunk = buf.slice(0, i);
            buf = buf.slice(i + 2);
            const data = chunk.split('\n').filter((l) => l.startsWith('data: ')).map((l) => l.slice(6)).join('\n');
            if (!data) continue;
            try {
              const ev = JSON.parse(data);
              if (ev && typeof ev.topic === 'string') onEvent(ev);
            } catch { /* hello frame */ }
          }
        }
      } catch {
        /* reconnect */
      }
      onState?.(false);
      if (stopped) break;
      await new Promise((r) => setTimeout(r, wait));
      wait = Math.min(wait * 2, 15000);
    }
  })();
  return () => { stopped = true; ctrl?.abort(); };
}
